import { Star, Calendar, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import type { Doctor } from "@/hooks/useDoctors";
import { doctorUrl } from "@/lib/slugs";
import { getDepartmentColor } from "@/lib/departmentColors";
import docMaleImage from "@/assets/Doctor Portrait Avatar.jpg";
import docFemaleImage from "@/assets/Doctor Portrait Avatar (1).jpg";

const PRESERVED_ABBREVIATIONS = new Set([
  "ENT", "ICU", "IVF", "MD", "MS", "MBBS", "MCH", "DM", "DNB", "FCPS", "FRCS", "MRCP", "MRCOG", "BDS", "MDS", "OB", "GYN", "OBGYN"
]);

function cleanTitleCase(text: string): string {
  if (!text) return "";
  return text
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .map((word) => {
      if (word.includes("-") || word.includes("/")) {
        const separator = word.includes("-") ? "-" : "/";
        return word
          .split(separator)
          .map((w) => {
            const upper = w.toUpperCase();
            const cleanUpper = upper.replace(/[^A-Z]/g, "");
            if (PRESERVED_ABBREVIATIONS.has(cleanUpper)) return upper;
            if (w === "&" || w === "/") return w;
            return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
          })
          .join(separator);
      }
      const upper = word.toUpperCase();
      const cleanUpper = upper.replace(/[^A-Z]/g, "");
      if (PRESERVED_ABBREVIATIONS.has(cleanUpper)) return upper;
      if (word === "&" || word === "/") return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

import { useState } from "react";

export function DoctorCard({ d }: { d: Doctor }) {
  const placeholder =
    d.gender === "Female" ? docFemaleImage : docMaleImage;

  const [loaded, setLoaded] = useState(false);
  const deptColorHex = getDepartmentColor(d.department);

  const primarySpecialty = Array.isArray(d.specialties)
  ? d.specialties[0]
  : d.specialties?.split(/\n|,/)[0]?.trim();

  return (
    <Link
      to={doctorUrl(d.department, d.practitioner_name)}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-500 hover:-translate-y-2 hover:border-gold/30 hover:shadow-elevated"
    >
      {/* Department color stripe */}
      <div
        className="h-2.5 w-full shrink-0"
        style={{ backgroundColor: deptColorHex }}
        aria-hidden
      />

      {/* Image */}
      <div className="relative aspect-3/4 overflow-hidden bg-primary-soft">
        {/* Placeholder while loading */}
        {!loaded && (
          <img
            src={placeholder}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        {/* Actual image */}
        <img
          src={d.image || placeholder}
          
          alt={d.practitioner_name}
          loading="lazy"
          width={1024}
          height={1280}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            e.currentTarget.src = placeholder;
            setLoaded(true);
          }}
          className={`h-full w-full object-cover transition-all duration-500 group-hover:scale-105 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />

        <div className="absolute inset-0 bg-linear-to-t from-primary/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-6">
        <div className="space-y-1">
          {d.department && (
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold">
              {d.department}
            </p>
          )}

          <h3 className="font-display font-space text-2xl font-semibold leading-tight text-primary">
            {d.practitioner_name}
          </h3>

          {(primarySpecialty || d.role) && (
            <p className="pb-2 text-sm font-medium text-muted-foreground">
              {cleanTitleCase(primarySpecialty || d.role || "")}
            </p>
          )}
        </div>

        <div className="mt-auto">
          {d.experience && (
            <div className="my-2 grid grid-cols-2 gap-4 border-y border-border/50 py-2">
              <div className="space-y-0.5">
                <span className="block text-[9px] font-bold uppercase tracking-widest text-muted-foreground/70">
                  Experience
                </span>
                <span className="block text-[13px] font-semibold text-primary">
                  {d.experience}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/10 transition-all duration-300 group-hover:shadow-xl group-hover:shadow-primary/20">
            <Calendar className="h-3.5 w-3.5" />
            View or Book
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 h-1 w-0 bg-linear-to-r from-gold to-gold/50 transition-all duration-700 group-hover:w-full" />
    </Link>
  );
}